'use client';
import type { FagmoblerAlgoliaProduct } from '@fagmobler/types';
import type { OmniumStore } from '@fagmobler/omnium';

import { calculatePercentOff, Price } from '../price';
import { Promotion } from '../promotion';
import {
  ProductTags,
  StickerProps,
  StockIndicators,
  ProductListGridItemImage,
} from '../../components';
import clsx from 'clsx';
import { MouseEvent, useRef } from 'react';
import { ProductListGridItemColorVariants } from './ProductListGridItemColorVariants';

export type ProductListGridItemProps = {
  product: FagmoblerAlgoliaProduct;
  tags: { label: string; variant: StickerProps['variant'] }[];
  store?: OmniumStore | null;
  size?: 'sm' | 'md' | 'lg';
  image?: React.ReactNode;
  showImage?: boolean;
  onClick?(): void;
  onStoreClick?(event: MouseEvent): void;
  inventory?: {
    display: number;
    stock: number;
  };
};

export const ProductListGridItem = ({
  product,
  tags,
  store,
  size,
  image,
  showImage = true,
  inventory,
  onStoreClick,
}: ProductListGridItemProps) => {
  const productTagsRef = useRef<HTMLDivElement>(null);

  const {
    posterName,
    posterDescription,
    images,
    price,
    campaignPrice,
    memberPrice,
    stock,
    deliveryTimeText,
    colour_variants,
    space_variants,
    colour,
  } = product;

  const productStock = inventory || {
    display: (store?.id && stock && stock[`${store.id}Display`]) || 0,
    stock: (store?.id && stock && stock[`${store.id}Stock`]) || 0,
  };

  const discountInPercent =
    ((product?.tags?.includes('nyhet') ||
      product?.tags?.includes('salg') ||
      product?.tags?.includes('kupp') ||
      product?.tags?.includes('outlet') ||
      product?.tags?.includes('KUPP!') ||
      product?.tags?.includes('BLACK WEEK') ||
      product?.tags?.includes('Medlemstilbud')) &&
      !product?.tags?.includes('BLACK WEEK MEDLEM') &&
      price &&
      ((campaignPrice && calculatePercentOff(price, campaignPrice)) ||
        (memberPrice && calculatePercentOff(price, memberPrice)))) ||
    0;

  const calculateOffset = () => {
    if (productTagsRef.current) {
      return (
        productTagsRef.current.clientHeight +
        productTagsRef.current.offsetTop * 2
      );
    }
    return 45; // guestimate for a single row of offsets
  };

  return (
    <div className="relative w-full max-w-sm group">
      {showImage && (
        <div
          className={clsx(
            'overflow-hidden relative group-hover:bg-neutral-40 block',
            'w-full lg:h-50 aspect-square',
            'bg-neutral-30 rounded-[4px]',
            'outline-offset-8 focus:outline-4'
          )}
        >
          <ProductTags
            tags={tags}
            variant="plp"
            size="xs"
            ref={productTagsRef}
          />

          <ProductListGridItemImage
            images={images}
            image={image}
            size="md"
            topOffset={calculateOffset()}
          />
          {discountInPercent > 0 && (
            <Promotion
              className="absolute bottom-2 right-2 z-10"
              size="sm"
              innerClassName={
                product?.tags?.includes('BLACK WEEK')
                  ? 'bg-[#F4A933] text-neutral-80'
                  : ''
              }
              percent={discountInPercent}
            />
          )}
        </div>
      )}
      <div className="my-4 flex justify-between flex-col gap-4">
        <h2 className="hd-base mb-0 pb-0 text-neutral-80">
          {/*Replace &nbsp; with space so it doesnt break layout*/}
          {posterName &&
            posterName.replace(new RegExp(String.fromCharCode(160), 'g'), ' ')}
          <span className="block body-xs text-neutral-70">
            {posterDescription}
          </span>
        </h2>

        {((colour_variants && !!colour_variants.length) ||
          (space_variants && !!space_variants.length)) && (
          <div>
            <span className="body-xs">Flere alternativer:</span>
            <ProductListGridItemColorVariants
              activeColor={Array.isArray(colour) ? colour[0] : colour ?? ''}
              colors={colour_variants ?? []}
              space={space_variants ?? []}
            />
          </div>
        )}

        {store?.id && (
          <StockIndicators
            stock={productStock}
            store={store}
            onStoreClick={onStoreClick}
            notAvailable={product.tags?.includes('outlet')}
            deliveryTime={deliveryTimeText}
            algoliaProduct={product}
          />
        )}

        <Price
          size="sm"
          price={price}
          memberPrice={memberPrice}
          campaignPrice={campaignPrice}
          withStickers={false}
          isBlackFriday={product?.tags?.includes('BLACK WEEK MEDLEM') || false}
        />
      </div>
    </div>
  );
};
